import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertBox,
  Button,
  ChainSelector,
  Input,
  SvgIcon,
  SelectOption,
  InputSelect,
} from '@/components';
import { useInputText, useDebounce, useApiCall } from '@/hooks';
import { Validation } from '@/models';
import {
  ChainsEnum,
  AssetsEnum,
  ChainsDictionary,
  DEBOUNCE_DELAY,
} from '@/config';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceActions } from '@/redux/actions';
import {
  amlTraceSelectors,
  serviceOptionsSelectors,
  userSelectors,
} from '@/redux/selectors';
import {
  amlbotAPI,
  WhichHashRequest,
  AmlTraceRequest,
  AssetTokenRequest,
  AssetTokenResponse,
} from '@/api';
import { showEmailConfirmationToast } from '@/helpers';

import styles from './TraceInvestigationForm.module.sass';

const PRICE_PER_CHECK = 10;
const DEFAULT_NETWORK_TOKEN_ID = '0';
const AVAILABLE_TOKENS: AssetsEnum[] = [AssetsEnum.USDT, AssetsEnum.USDC];

interface TraceInvestigationFormProps {
  isDisabled: boolean;
}

const TraceInvestigationForm = ({
  isDisabled,
}: TraceInvestigationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [assetTokens, setAssetTokens] = useState<SelectOption[]>([]);
  const [tokenFilter, setTokenFilter] = useState({ page: 1, term: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMoreTokens, setHasMoreTokens] = useState(true);
  const [selectedToken, setSelectedToken] = useState<SelectOption | null>(null);

  const { data: serviceOptionData } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );

  const { loading, error } = useAppSelector(amlTraceSelectors.getTraceSlice);
  const pendingSubmitRef = useRef(false);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);

  const traceCheckCost = serviceOptionData?.traceCheckCost || PRICE_PER_CHECK;

  const tracerCurrencies = useAppSelector(
    amlTraceSelectors.getTracerCurrencies,
  );

  const [whichLoading, setWhichLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Validations
  const HASH_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const CHAIN_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const TOKEN_VALIDATIONS: Validation[] = useMemo(() => {
    const validations: Validation[] = [
      {
        name: 'minLength',
        value: 0,
        message: t('translation.field_is_required'),
      },
    ];

    return validations;
  }, [t]);

  // Input fields
  const hashField = useInputText('', HASH_VALIDATIONS);
  const chainField = useInputText('', CHAIN_VALIDATIONS);
  const tokenField = useInputText(DEFAULT_NETWORK_TOKEN_ID, TOKEN_VALIDATIONS);

  useEffect(() => {
    if (!tracerCurrencies?.length) {
      dispatch(amlTraceActions.fetchCurrencies());
    }
  }, [dispatch, tracerCurrencies]);

  useEffect(() => {
    tokenField.onChange(DEFAULT_NETWORK_TOKEN_ID);
    setAssetTokens([]);
    setTokenFilter({ page: 1, term: '' });
  }, [chainField.value]);

  // Chain options with disabled state from WhichHash
  const baseChains = useMemo(() => {
    return tracerCurrencies.map((chain) => ({
      label: ChainsDictionary[chain].name,
      value: chain,
    }));
  }, [tracerCurrencies]);

  const [chainOptions, setChainOptions] = useState(baseChains);

  useEffect(() => {
    setChainOptions(baseChains);
  }, [baseChains]);

  const resetOptions = useCallback(() => {
    setChainOptions(baseChains);
  }, [baseChains]);

  const debouncedRequest = useDebounce(async () => {
    if (!hashField.value) {
      resetOptions();
      return;
    }

    try {
      setWhichLoading(true);

      const payload: WhichHashRequest = {
        hash: hashField.value,
      };

      const response = await amlbotAPI.getWhichHash(payload);

      const {
        data: {
          suggestions: { txs: suggestedChains },
        },
      } = response;

      setSuggestions(suggestedChains);

      if (
        suggestedChains.length === 0 ||
        (chainField.value &&
          !suggestedChains.includes(chainField.value as ChainsEnum))
      ) {
        chainField.onChange('');
      }

      setChainOptions((prev) =>
        prev.map((option) => ({
          ...option,
          isDisabled: !suggestedChains.includes(option.value as ChainsEnum),
        })),
      );
    } catch (err: unknown) {
      console.error(err);
    } finally {
      setWhichLoading(false);
    }
  }, DEBOUNCE_DELAY);

  const hashFieldChangeHandler = (newValue: string) => {
    hashField.onChange(newValue.trim());
    chainField.onReset();
    tokenField.onReset();
    debouncedRequest();
  };

  const [getAssetTokens] = useApiCall(async () => {
    const payload: AssetTokenRequest = {
      asset: chainField.value,
      page: tokenFilter.page,
      term: tokenFilter.term,
    };
    // Optionally [errorCode] - ErrorCodeEnum.FIELD_REQUIRED
    const response: AssetTokenResponse = await amlbotAPI.fetchTokens(payload);

    if (response.success) {
      setHasMoreTokens(!!response.data?.hasMore);

      const tokenList = (response.data?.result || []).map(
        (token): SelectOption => ({
          value: `${token.token_id}`,
          label: `${token.code} [${token.issuer}]`,
          code: token.code,
        }),
      );

      if (response.data?.page === 1) {
        setAssetTokens(() => [
          {
            value: `0`,
            label: `${ChainsDictionary[chainField.value].symbol} [${t(
              'translation.native_coin',
            )}]`,
            code: chainField.value,
          },
          ...(selectedToken ? [selectedToken] : []),
          ...tokenList,
        ]);
      } else {
        const uniqTokens = selectedToken
          ? tokenList.filter((token) => selectedToken.value !== token.value)
          : tokenList;
        setAssetTokens((currentOptions) => [...currentOptions, ...uniqTokens]);
      }
    }
  });

  useEffect(() => {
    if (
      !serviceOptionData?.hasTokensCoins.includes(chainField.value) ||
      !hasMoreTokens ||
      suggestions.length === 0
    ) {
      return;
    }

    getAssetTokens();
  }, [chainField.value, tokenFilter.page, tokenFilter.term]);

  const submitDisabled =
    isDisabled ||
    whichLoading ||
    !hashField.isValid ||
    !chainField.isValid ||
    !tokenField.isValid ||
    suggestions.length === 0;

  const clearFormAndScrollToHistory = useCallback(() => {
    hashField.onReset();
    chainField.onReset();
    tokenField.onReset();
    setSuggestions([]);
    resetOptions();

    setTimeout(() => {
      document.getElementById('trace-history-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  }, [hashField, chainField, tokenField, resetOptions]);

  useEffect(() => {
    if (!loading && pendingSubmitRef.current && !error) {
      pendingSubmitRef.current = false;
      clearFormAndScrollToHistory();
    }
  }, [loading, error, clearFormAndScrollToHistory]);

  const checkHandler = useCallback(() => {
    if (isEmailConfirmed) {
      if (!loading) {
        if (hashField.isValid && chainField.isValid) {
          pendingSubmitRef.current = true;
          const payload: AmlTraceRequest = {
            network: chainField.value as ChainsEnum,
            asset:
              selectedToken?.code || ChainsDictionary[chainField.value].symbol,
            hash: hashField.value,
          };
          dispatch(amlTraceActions.startTrace(payload));
        } else {
          if (!hashField.isValid) {
            hashField.onBlur();
          }
          if (!chainField.isValid) {
            chainField.onBlur();
          }
        }
      }
    } else {
      showEmailConfirmationToast();
    }
  }, [
    dispatch,
    selectedToken,
    hashField,
    chainField,
    isEmailConfirmed,
    loading,
  ]);

  const onChainChanged = (value: string) => {
    chainField.onChange(value);
    tokenField.onReset();
  };

  const searchTermChanged = useDebounce(() => {
    setTokenFilter({ page: 1, term: searchTerm });
    setHasMoreTokens(true);
  });

  const tokenTermChanged = (val: string) => {
    setSearchTerm(val);
    searchTermChanged();
  };

  const handleScrollBottom = () => {
    if (hasMoreTokens) {
      setTokenFilter({ ...tokenFilter, page: tokenFilter.page + 1 });
    }
  };

  const handleTokenOnChange = (val: string) => {
    if (val !== DEFAULT_NETWORK_TOKEN_ID) {
      setSelectedToken(() => {
        const token = assetTokens.find((option) => {
          return option.value === val;
        });

        return token || null;
      });
    } else {
      setSelectedToken(() => null);
    }

    tokenField.onChange(val);
  };

  return (
    <div className={styles.root}>
      <div className={styles.field}>
        <Input
          id="ai-investigation-hash"
          name="ai-investigation-hash"
          type="text"
          value={hashField.value}
          placeholder={t('translation.trace_tx_hash')!}
          onChange={hashFieldChangeHandler}
          onBlur={hashField.onBlur}
          isError={hashField.isDirty && !hashField.isValid}
          readOnly={whichLoading}
          disabled={isDisabled}
          clearable
          required
        />
        {hashField.isDirty && !hashField.isValid && (
          <AlertBox type="danger" small>
            {{
              description: hashField.errors[0],
            }}
          </AlertBox>
        )}
      </div>

      <div className={styles.hint}>
        <SvgIcon name="info-circle" className={styles.hintIcon} />
        <span>{t('translation.ai_investigation_hint')}</span>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionLabel}>
          {t('translation.ai_investigation_specify_blockchain')}
        </div>
        <ChainSelector
          list={chainOptions}
          onSelect={(val) => onChainChanged(val)}
          limited={false}
          isDisabled={isDisabled}
          modeChains={[]}
        />
      </div>

      <div className={styles.section}>
        {serviceOptionData?.hasTokensCoins.includes(chainField.value) &&
          suggestions.length > 0 && (
            <>
              <div className={styles.sectionLabel}>
                {t('translation.ai_investigation_choose_token')}
              </div>
              <div className={styles.fields}>
                <div className={styles.tokens}>
                  <InputSelect
                    id="tokens"
                    name="tokens"
                    classNamePrefix="chain-select"
                    value={tokenField.value}
                    onChange={handleTokenOnChange}
                    onBlur={tokenField.onBlur}
                    options={assetTokens}
                    placeholder="Select"
                    isSearchable
                    clearable
                    disabled={loading || whichLoading}
                    onMenuScrollToBottom={handleScrollBottom}
                    onInputChange={tokenTermChanged}
                    inputValue={searchTerm}
                  />
                </div>
              </div>
            </>
          )}
      </div>

      <Button
        corner="rect"
        className={styles.submit}
        onClick={checkHandler}
        disabled={submitDisabled}
      >
        <span className={styles.submitLabel}>
          {whichLoading
            ? t('translation.btn_validating_state')
            : t('translation.ai_investigation_cta_prefix')}
        </span>
        <span className={styles.submitPrice}>
          <SvgIcon name="aml" />
          {traceCheckCost}
        </span>
      </Button>
    </div>
  );
};

export default TraceInvestigationForm;
